import useCrud from 'hooks/useCrud';
import { toUpperCase } from 'utils/toLowerCase';
import { resolve } from 'services/api';
import { getRegionsStoreCount, RegionCountsMap } from 'services/api/admin';
import { getDepartmentDetail } from 'services/api/department/department';
import { getWabaAccountCount } from 'services/api/whatsapp';
import { PlatformConfig, RegionConfig, Store } from './share';
import { useMemo } from 'react';

export const getDepartmentScopes = (departmentId: number) => {
  return getDepartmentDetail(departmentId).then((res) => res.platformStoreScopes);
};

export const useConfigList = ({
  supportWa,
  departmentId,
  platform,
  storeDataSource,
  permId,
}: {
  supportWa: boolean;
  departmentId?: number;
  platform?: General.PlatformUpperCaseTunnel;
  storeDataSource?: Store[];
  permId?: General.PermissionId;
}) => {
  const {
    loading,
    item: configList = [],
    fetch,
  } = useCrud({
    read: () => {
      if (storeDataSource) {
        const platformConfigs = storeDataSource.reduce<PlatformConfig[]>((res, store) => {
          const { externalType: platform, region } = store;

          const platformConfig = res.find((_) => _.platform === platform);

          if (!platformConfig) {
            res.push({
              platform,
              count: 1,
              regionList: [
                {
                  region,
                  count: 1,
                },
              ],
            });

            return res;
          }

          platformConfig.count += 1;

          const regionConfig = platformConfig.regionList.find((_) => _.region === region);

          if (!regionConfig) {
            platformConfig.regionList.push({
              region,
              count: 1,
            });

            return res;
          }

          regionConfig.count += 1;

          return res;
        }, []);

        return Promise.resolve(platformConfigs);
      }

      const getRegionList = (regionCountsMap: RegionCountsMap) => {
        return Object.keys(regionCountsMap).map((region) => {
          return {
            region: toUpperCase(region.replace(/^(\w{2,})Cnt$/, '$1')) as General.UpperCaseRegion,
            count: regionCountsMap[region],
          };
        });
      };

      const defaultEmpty = {} as RegionCountsMap;
      if (departmentId) {
        return Promise.all([
          resolve(getRegionsStoreCount()),
          getDepartmentScopes(departmentId),
        ]).then(([regionStoreCount, scopes]) => {
          return scopes.reduce<PlatformConfig[]>((res, platformScope) => {
            const { platform, storeCount, regionStoreScopes } = platformScope;

            if (storeCount === 0) {
              return res;
            }

            res.push({
              platform,
              count: storeCount,
              regionList: regionStoreScopes.reduce<RegionConfig[]>((res, regionScope) => {
                const { region, storeCount } = regionScope;

                if (region === 'all') {
                  if (platform === 'WA_BIZ') {
                    return res;
                  }

                  const {
                    shopeeCountData,
                    lazadaCountData,
                    tokopediaCountData = defaultEmpty,
                    tiktokCountData,
                    tiktokUsCountData = defaultEmpty,
                    aliExpressCountData = defaultEmpty,
                    mercadoCountData = defaultEmpty,
                    kwaiCountData = defaultEmpty,
                    facebookCountData = defaultEmpty,
                    jolectCountData = defaultEmpty,
                  } = regionStoreCount;

                  res.push(
                    ...getRegionList(
                      {
                        SHOPEE: shopeeCountData,
                        LAZADA: lazadaCountData,
                        TOKOPEDIA: tokopediaCountData,
                        TIKTOK: tiktokCountData,
                        TIKTOK_US: tiktokUsCountData,
                        ALIEXPRESS: aliExpressCountData,
                        MERCADO: mercadoCountData,
                        KWAI: kwaiCountData,
                        FACEBOOK: facebookCountData,
                        JOLECT: jolectCountData,
                      }[platform],
                    ),
                  );

                  return res;
                }

                res.push({
                  region,
                  count: storeCount,
                });

                return res;
              }, []),
            });

            return res;
          }, []);
        });
      }

      return Promise.all([
        resolve(getRegionsStoreCount(permId)),
        supportWa ? getWabaAccountCount() : Promise.resolve(0),
      ]).then(([regionStoreCount, wabaCount]) => {
        const {
          shopeeCountData,
          lazadaCountData,
          tokopediaCountData = defaultEmpty,
          tiktokCountData,
          tiktokUsCountData = defaultEmpty,
          aliExpressCountData = defaultEmpty,
          mercadoCountData = defaultEmpty,
          kwaiCountData = defaultEmpty,
          facebookCountData = defaultEmpty,
          jolectCountData = defaultEmpty,
        } = regionStoreCount;

        const getPlatformStoreCount = (regionCountsMap: RegionCountsMap) => {
          return Object.keys(regionCountsMap).reduce(
            (res, region) => res + regionCountsMap[region],
            0,
          );
        };

        const list: PlatformConfig[] = [
          {
            platform: 'SHOPEE',
            count: getPlatformStoreCount(shopeeCountData),
            regionList: getRegionList(shopeeCountData),
          },
          {
            platform: 'LAZADA',
            count: getPlatformStoreCount(lazadaCountData),
            regionList: getRegionList(lazadaCountData),
          },
          {
            platform: 'TOKOPEDIA',
            count: getPlatformStoreCount(tokopediaCountData),
            regionList: getRegionList(tokopediaCountData),
          },
          {
            platform: 'TIKTOK',
            count: getPlatformStoreCount(tiktokCountData),
            regionList: getRegionList(tiktokCountData),
          },
          {
            platform: 'TIKTOK_US',
            count: getPlatformStoreCount(tiktokUsCountData),
            regionList: getRegionList(tiktokUsCountData),
          },
          {
            platform: 'ALIEXPRESS',
            count: getPlatformStoreCount(aliExpressCountData),
            regionList: getRegionList(aliExpressCountData),
          },
          {
            platform: 'MERCADO',
            count: getPlatformStoreCount(mercadoCountData),
            regionList: getRegionList(mercadoCountData),
          },
          {
            platform: 'KWAI',
            count: getPlatformStoreCount(kwaiCountData),
            regionList: getRegionList(kwaiCountData),
          },
          {
            platform: 'FACEBOOK',
            count: getPlatformStoreCount(facebookCountData),
            regionList: getRegionList(facebookCountData),
          },
          {
            platform: 'WA_BIZ',
            count: wabaCount,
            regionList: [],
          },
          {
            platform: 'JOLECT',
            count: getPlatformStoreCount(jolectCountData),
            regionList: [],
          },
        ];

        if (platform) {
          return list.filter((item) => item.platform === platform);
        }

        return list.filter(({ count }) => count !== 0);
      });
    },
  });

  const sortConfigList = useMemo(() => {
    const sort: PlatformConfig['platform'][] = [
      'SHOPEE',
      'LAZADA',
      'TOKOPEDIA',
      'TIKTOK',
      'TIKTOK_US',
      'ALIEXPRESS',
      'MERCADO',
      'KWAI',
      'FACEBOOK',
      'WA_BIZ',
    ];
    return configList.sort((prev, next) => {
      const prevIndex = sort.indexOf(prev.platform);
      const nextIndex = sort.indexOf(next.platform);
      if (prevIndex < 0 || nextIndex < 0) return -1;
      return prevIndex - nextIndex;
    });
  }, [configList]);

  return {
    loading,
    configList: sortConfigList,
    fetch,
  };
};
